import { createSelector } from "@reduxjs/toolkit";

import type { IPager, ISearchTableRow } from "@/shop/types/common";
import type { ISearchTable } from "@/shop/types/searchTable";
import { deriveSearchFromRaw } from "@/shop/utils/DataConversion/deriveSearchFromRaw";

import { PAGE_SIZE_OPTIONS } from "./constants";
import type { ITreeSearchReducerState } from "./types";

const getSearchState = (state: { partTreeSearch: ITreeSearchReducerState }): ITreeSearchReducerState => state.partTreeSearch;

const getSearchResults = createSelector(getSearchState, (state) => state.materialsResult);

export const getSearchResultsIsFetching = createSelector(getSearchState, (state) => state.isFetching);

export const getSearchResultsHasError = createSelector(getSearchState, (state) => state.hasError);

export const getSearchFields = createSelector(getSearchResults, (data) => data?.searchFields ?? []);

export const getSearchTableRawHeader = createSelector(getSearchResults, (data) => (data != null ? data.header : []));

export const getSearchTableHeader = createSelector(getSearchTableRawHeader, (header) => header);

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const getSearchTableRawData = createSelector(getSearchResults, (data) => (data != null ? data.data : []));

export const getSearchTableRows = createSelector(getSearchResults, deriveSearchTableRows);

// -gl- Clickable Drawings -> manipulate core-app Server Response to allow MaterialSearch to render the data
// without creating a whole new Component - what would surely be the better solution but there's no budget.
export function deriveSearchTableRows(result: ISearchTable): ISearchTableRow[] {
  if (!result || !result.data) {
    return [];
  }

  const { header, data, flags } = result;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return deriveSearchFromRaw(data, header, flags);
}

export const getSearchTableQuery = createSelector(getSearchState, (state) => state.materialsQuery);

export const getSearchTableFilter = createSelector(getSearchTableQuery, (query) => (query && query.where) || {});

export const getSearchResultsUrl = createSelector(getSearchState, (state) => state.materialsUrl);

export const getSearchResultCount = createSelector(getSearchResults, (result) => (result ? result.count : 0));

export const getPagerOffset = createSelector(getSearchTableQuery, (query) => (query.offset ? query.offset : 0));
export const getPagerLimit = createSelector(getSearchTableQuery, (query) => (query.limit ? query.limit : 0));
export const getPagerSort = createSelector(getSearchTableQuery, (query) => query.sort);

export const getServerPager = createSelector(getSearchResultCount, getPagerOffset, getPagerLimit, getPagerSort, (count, offset, limit, sort) => ({
  count,
  limit,
  offset,
  sort
}));

export const getSearchPager = createSelector(getServerPager, deriveClientPager);

export const getAdvancedPOSTLoading = createSelector(getSearchState, (state) => state.advancedPOSTLoading);

// TODO: move to common funcs
export function deriveClientPager({ offset, limit, count, sort }: { count: number; limit: number; offset: number; sort: string | undefined }): IPager {
  const pageSize = limit;
  const currentPage = Math.floor(offset / limit) + 1;
  const numPages = Math.max(currentPage, Math.ceil((count ? count : 0) / limit));
  const pageSizeOptions = PAGE_SIZE_OPTIONS;
  return {
    numPages,
    currentPage,
    pageSize,
    pageSizeOptions,
    sort
  };
}
